import { FloppyDiskIcon } from '@phosphor-icons/react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PageHeader, Spinner } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useGcrsDetails } from '../api';
import { GcrForm } from '../components';

export const GcrDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    uploadUrlData,
    isFetching,
    refetchUploadUrl,
    updateGcrMutation,
    optionsData,
  } = useGcrsDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      sizeId: details.size.id,
      typeId: details.type.id,
      colorId: details.color.id,
      printerId: details.printer.id,
      filePublicUrl: details.filePublicUrl ?? '',
    };
  }, [details]);

  const updateGcrFile = async (data) => {
    if (uploadUrlData?.gcrId) {
      try {
        await updateGcrMutation.mutateAsync({
          name: data.name,
          printerId: data.printerId,
          typeId: data.typeId,
          sizeId: data.sizeId,
          colorId: data.colorId,
          id: params.id,
        });
        toast.success(`Gcr “${data.name}” updated successfully!`);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <GcrForm
      options={optionsData}
      onSubmit={updateGcrFile}
      gcrDetails={details}
      defaultValues={defaultValues}
      uploadUrlData={uploadUrlData}
      refetchUploadCredentials={refetchUploadCredentials}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Gcrs}
          actions={[
            <Button
              key="save-gcr-file"
              type="submit"
              data-testid="save-gcr-file"
              isLoading={isFetching || isLoading || updateGcrMutation.isPending}
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </GcrForm>
  );
};
